/**
 * Tamil Calendar (Tamil Panchangam) Utility Functions
 *
 * The Tamil calendar is a sidereal solar calendar used in Tamil Nadu, India.
 * It follows the solar year and has 12 months aligned with zodiac signs.
 * The Tamil year (Tamil Varudam) typically starts in mid-April.
 */

// Tamil month names in native script (தமிழ் மாதங்கள்)
export const tamilMonths = [
  { name: 'சித்திரை', transliteration: 'Chithirai' },    // Apr 14 - May 14 (31 days)
  { name: 'வைகாசி', transliteration: 'Vaikasi' },        // May 15 - Jun 15 (32 days)
  { name: 'ஆனி', transliteration: 'Aani' },              // Jun 16 - Jul 16 (31 days)
  { name: 'ஆடி', transliteration: 'Aadi' },              // Jul 17 - Aug 16 (31 days)
  { name: 'ஆவணி', transliteration: 'Aavani' },          // Aug 17 - Sep 16 (31 days)
  { name: 'புரட்டாசி', transliteration: 'Purattasi' },   // Sep 17 - Oct 17 (31 days)
  { name: 'ஐப்பசி', transliteration: 'Aippasi' },       // Oct 18 - Nov 16 (30 days)
  { name: 'கார்த்திகை', transliteration: 'Karthigai' },  // Nov 17 - Dec 15 (29 days)
  { name: 'மார்கழி', transliteration: 'Margazhi' },     // Dec 16 - Jan 14 (30 days)
  { name: 'தை', transliteration: 'Thai' },               // Jan 15 - Feb 12 (29 days)
  { name: 'மாசி', transliteration: 'Maasi' },           // Feb 13 - Mar 13 (29 days)
  { name: 'பங்குனி', transliteration: 'Panguni' }       // Mar 14 - Apr 13 (31 days)
];

// Tamil month start dates (approx. same each year in Gregorian calendar)
// Each month is approximately 29-32 days based on solar transit (Sankranti)
// [month (0-11), date]
// These dates are based on traditional Tamil Panchangam
const monthStartDates = [
  { month: 3, date: 14 },   // Chithirai (Aries): Apr 14 - May 14 (31 days)
  { month: 4, date: 15 },   // Vaikasi (Taurus): May 15 - Jun 15 (32 days)
  { month: 5, date: 16 },   // Aani (Gemini): Jun 16 - Jul 16 (31 days)
  { month: 6, date: 17 },   // Aadi (Cancer): Jul 17 - Aug 16 (31 days)
  { month: 7, date: 17 },   // Aavani (Leo): Aug 17 - Sep 16 (31 days)
  { month: 8, date: 17 },   // Purattasi (Virgo): Sep 17 - Oct 17 (31 days)
  { month: 9, date: 18 },   // Aippasi (Libra): Oct 18 - Nov 16 (30 days)
  { month: 10, date: 17 },  // Karthigai (Scorpio): Nov 17 - Dec 15 (29 days)
  { month: 11, date: 16 },  // Margazhi (Sagittarius): Dec 16 - Jan 14 (30 days)
  { month: 0, date: 15 },   // Thai (Capricorn): Jan 15 - Feb 12 (29 days)
  { month: 1, date: 13 },   // Maasi (Aquarius): Feb 13 - Mar 13 (29 days)
  { month: 2, date: 14 }    // Panguni (Pisces): Mar 14 - Apr 13 (31 days)
];

/**
 * Convert Gregorian date to Tamil calendar date
 * @param {Date} gregorianDate - JavaScript Date object
 * @returns {Object} - { monthIndex, monthName, date, year }
 */
export const gregorianToTamil = (gregorianDate) => {
  const year = gregorianDate.getFullYear();
  const month = gregorianDate.getMonth();
  const date = gregorianDate.getDate();

  // Find which Tamil month the date falls into
  let tamilMonthIndex = -1;
  let tamilDate = 1;

  // Check each Tamil month
  for (let i = 0; i < monthStartDates.length; i++) {
    const currentMonthStart = monthStartDates[i];
    const nextMonthStart = monthStartDates[(i + 1) % 12];

    // Create Date objects for start of current and next Tamil month
    let currentStartDate = new Date(year, currentMonthStart.month, currentMonthStart.date);
    let nextStartDate = new Date(year, nextMonthStart.month, nextMonthStart.date);

    // Handle year boundaries correctly:
    // If we're in Jan-Mar (months 0-2) and checking a Tamil month that starts in Apr-Dec (3-11),
    // the Tamil month started in the PREVIOUS Gregorian year
    if (month < 3 && currentMonthStart.month >= 3) {
      currentStartDate.setFullYear(year - 1);
    }

    // If next month starts before current month (wraps to next year)
    if (nextMonthStart.month < currentMonthStart.month) {
      // Next month is in the next year
      nextStartDate.setFullYear(currentStartDate.getFullYear() + 1);
    } else if (month < 3 && nextMonthStart.month >= 3) {
      // Both current and next in previous year scenario
      nextStartDate.setFullYear(year - 1);
    }

    // Check if the date falls in this Tamil month
    if (gregorianDate >= currentStartDate && gregorianDate < nextStartDate) {
      tamilMonthIndex = i;
      // Calculate Tamil date (days since month start + 1)
      tamilDate = Math.floor((gregorianDate - currentStartDate) / (1000 * 60 * 60 * 24)) + 1;
      break;
    }
  }

  // If month not found, default to first month
  if (tamilMonthIndex === -1) {
    tamilMonthIndex = 0;
    tamilDate = 1;
  }

  // Calculate Tamil year
  // Tamil year changes with Chithirai (month index 0) in April
  // Tamil era is not counted from a specific historical year like Malayalam Kollavarsham
  // Instead, we use the Thiruvalluvar year which started in 31 BCE
  // However, most Tamil calendars just show the current Gregorian year + offset
  let tamilYear = year + 1971; // Thiruvalluvar year calculation

  // If we're before Chithirai month (months 9-11 in our array which are Thai to Panguni)
  // we're still in the previous Tamil year
  if (tamilMonthIndex >= 9) {
    tamilYear = year + 1970;
  }

  return {
    monthIndex: tamilMonthIndex,
    monthName: tamilMonths[tamilMonthIndex].name,
    transliteration: tamilMonths[tamilMonthIndex].transliteration,
    date: tamilDate,
    year: tamilYear
  };
};

/**
 * Get Tamil weekday names
 * @returns {Array} - Array of Tamil weekday names starting from Sunday
 */
export const getTamilWeekdays = () => {
  return [
    'ஞாயிறு',      // Sunday (Gnayiru)
    'திங்கள்',      // Monday (Thingal)
    'செவ்வாய்',     // Tuesday (Sevvai)
    'புதன்',        // Wednesday (Puthan)
    'வியாழன்',      // Thursday (Viyazhan)
    'வெள்ளி',       // Friday (Velli)
    'சனி'           // Saturday (Sani)
  ];
};

/**
 * Get all days in a Tamil month for display in calendar
 * @param {Date} gregorianDate - Current date to determine which Tamil month
 * @returns {Array} - Array of day objects with Tamil and Gregorian info
 */
export const getTamilMonthDays = (gregorianDate) => {
  const currentTamilDate = gregorianToTamil(gregorianDate);
  const monthIndex = currentTamilDate.monthIndex;

  // Get start date of current Tamil month
  const startDateInfo = monthStartDates[monthIndex];
  const year = gregorianDate.getFullYear();

  // Adjust year for months that fall in next calendar year
  const adjustedYear = startDateInfo.month < 3 && gregorianDate.getMonth() >= 3
    ? year + 1
    : year;

  const monthStartDate = new Date(adjustedYear, startDateInfo.month, startDateInfo.date);

  // Get start date of next Tamil month
  const nextMonthIndex = (monthIndex + 1) % 12;
  const nextStartDateInfo = monthStartDates[nextMonthIndex];
  const nextMonthYear = nextStartDateInfo.month < startDateInfo.month
    ? adjustedYear + 1
    : adjustedYear;
  const nextMonthStartDate = new Date(nextMonthYear, nextStartDateInfo.month, nextStartDateInfo.date);

  // Calculate number of days in Tamil month
  const daysInMonth = Math.floor((nextMonthStartDate - monthStartDate) / (1000 * 60 * 60 * 24));

  // Get starting day of week for Tamil month
  const startingDayOfWeek = monthStartDate.getDay();

  const days = [];

  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }

  // Add all days of the Tamil month
  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = new Date(monthStartDate);
    currentDate.setDate(monthStartDate.getDate() + day - 1);

    days.push({
      tamilDate: day,
      gregorianDate: currentDate.getDate(),
      gregorianMonth: currentDate.getMonth(),
      gregorianYear: currentDate.getFullYear(),
      fullDate: currentDate
    });
  }

  return days;
};
